import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Training = () => {
  const navigate = useNavigate();

  const benefits = [
    "Industry-specific expertise and real-world applications",
    "Flexible delivery options: on-site, virtual, or hybrid",
    "Professional certification opportunities",
    "Ongoing mentorship and support",
    "Measurable skill development outcomes",
    "Customized learning paths for different roles",
  ];

  const trainingPrograms = [
  {
    title: "Agile & Scrum Mastery",
    description: "Practical, certification-aligned training covering Scrum, Agile scaling, and adaptive delivery techniques.",
  },
  {
    title: "Leadership in Agile Organizations",
    description: "Develop leaders who can inspire, empower, and sustain high-performing Agile teams.",
  },
  {
    title: "Organizational Design & Operating Models",
    description: "Learn to design adaptive structures, roles, and governance that enable business agility.",
  },
  {
    title: "Change & Transformation Enablement",
    description: "Build internal capability to lead change, manage stakeholders, and embed new ways of working.",
  },
];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/")}
              className="text-2xl font-vailery text-primary hover:opacity-80 transition-opacity"
            >
              Orren Consultancy
            </button>
            <Button variant="outline" onClick={() => navigate("/")} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-90"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="w-20 h-20 mx-auto mb-6 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <GraduationCap className="h-10 w-10 text-white" />
            </div>
            <Badge variant="secondary" className="mb-6 bg-white/10 text-white border-white/20">
              Professional Development
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">Training</h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 animate-slide-up">
              Comprehensive training programs tailored to your industry needs, delivered by seasoned professionals with
              real-world expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center text-foreground">Program Benefits</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-border/50 bg-card/80 backdrop-blur-sm">
                  <CardContent className="p-6 flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground">{benefit}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Training Programs Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center text-foreground">Training Programs</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {trainingPrograms.map((program, index) => (
                <Card
                  key={index}
                  className="border-border/50 bg-card/80 backdrop-blur-sm hover:shadow-medium transition-all"
                >
                  <CardHeader>
                    <CardTitle className="text-xl tracking-wider">{program.title}</CardTitle>
                    <CardDescription className="text-base">{program.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-foreground">Invest in Your Team's Growth</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's discuss how our training programs can elevate your workforce
            </p>
            <Button
              size="lg"
              className="bg-gradient-primary shadow-medium"
              onClick={() => navigate("/#book-consultation")}
            >
              Book Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="text-3xl font-vailery mb-4">Orren</div>
            <p className="text-primary-foreground/80 mb-6">Professional Consulting Services</p>
            <div className="text-sm text-primary-foreground/60">© 2024 Orren. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Training;
